import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../data/mock';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowRight, 
  Filter, 
  X, 
  ExternalLink, 
  Clock, 
  Users,
  TrendingUp,
  Search,
  Grid,
  List,
  Play,
  Eye
} from 'lucide-react';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const categories = [
    { id: 'all', label: 'All Industries', icon: '🌐' },
    { id: 'schools', label: 'Schools', icon: '🏫' },
    { id: 'universities', label: 'Universities', icon: '🎓' },
    { id: 'hr', label: 'HR Teams', icon: '👥' },
    { id: 'startups', label: 'Startups', icon: '🚀' },
    { id: 'creators', label: 'Content Creators', icon: '🎨' }
  ];

  const serviceTypes = [
    { id: 'all', label: 'All Services', icon: '🔧' },
    { id: 'website', label: 'Web Development', icon: '💻' },
    { id: 'app', label: 'Mobile Apps', icon: '📱' },
    { id: 'design', label: 'UI/UX Design', icon: '🎨' },
    { id: 'marketing', label: 'Digital Marketing', icon: '📈' },
    { id: 'finance', label: 'Finance Solutions', icon: '💰' }
  ];

  const filteredPortfolio = mockData.portfolio.filter(project => {
    const categoryMatch = selectedCategory === 'all' || project.category === selectedCategory;
    const serviceMatch = selectedService === 'all' || project.service === selectedService;
    const searchMatch = 
      searchQuery === '' || 
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.technologies.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return categoryMatch && serviceMatch && searchMatch;
  });

  const ProjectModal = ({ project, onClose }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b z-10 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                {project.client.split(' ').map(word => word[0]).join('')}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
                <p className="text-gray-600">{project.client} • {project.year}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-blue-100 text-blue-700">
              {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
            </Badge>
            <Badge className="bg-green-100 text-green-700">
              {project.service.charAt(0).toUpperCase() + project.service.slice(1)}
            </Badge>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Project Image/Video */}
          <div className="relative h-64 md:h-80 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mb-6 overflow-hidden group">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-8xl font-serif font-bold text-black opacity-10">
                {project.client.split(' ').map(word => word[0]).join('')}
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            <div className="absolute bottom-4 left-4 text-white">
              <div className="text-sm opacity-90">Project Showcase</div>
              <div className="text-2xl font-bold">{project.title}</div>
            </div>
            <Button 
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm hover:bg-white"
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Live Preview
            </Button>
          </div>

          {/* Project Details */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="border-none shadow-sm">
              <CardContent className="p-6">
                <Clock className="w-6 h-6 text-blue-500 mb-3" />
                <div className="text-sm text-gray-600">Timeline</div>
                <div className="text-lg font-semibold text-gray-900">{project.timeline || '3 Months'}</div>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-sm">
              <CardContent className="p-6">
                <Users className="w-6 h-6 text-green-500 mb-3" />
                <div className="text-sm text-gray-600">Team Size</div>
                <div className="text-lg font-semibold text-gray-900">{project.teamSize || '5 Members'}</div>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-sm">
              <CardContent className="p-6">
                <TrendingUp className="w-6 h-6 text-amber-500 mb-3" />
                <div className="text-sm text-gray-600">ROI</div>
                <div className="text-lg font-semibold text-gray-900">{project.roi || '250% Increase'}</div>
              </CardContent>
            </Card>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Project Overview</h4>
            <p className="text-gray-700 leading-relaxed">{project.description}</p>
          </div>

          {/* Key Results */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Results & Impact</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {project.results.map((result, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-700">{result}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Technologies */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Technologies Used</h4>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <a href="/#contact">
  <Button className="bg-black text-white hover:bg-gray-800">
    Start Similar Project
  </Button>
</a>

          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-lime/20 to-blue-50/50" />
        <div className="container mx-auto max-w-6xl text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-200">
              🎯 500+ Projects Completed
            </Badge>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-black mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Portfolio</span>
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-10">
              Real projects, real results. See how we've transformed digital presence for businesses worldwide.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search projects by name, tech, or description..."
                  className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters & Controls */}
      <section className="sticky top-16 z-40 bg-white border-b border-gray-200 py-6 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Filter size={24} className="text-gray-600" />
              <span className="font-semibold text-gray-900">Filter Projects</span>
            </div>
            
            <div className="flex items-center gap-4">
              {/* View Toggle */}
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'bg-black text-white' : ''}
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-black text-white' : ''}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedService('all');
                  setSearchQuery('');
                }}
              >
                Clear All Filters
              </Button>
            </div>
          </div>

          {/* Filter Chips */}
          <div className="mt-6 space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-3">Industry Focus</div>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <motion.button
                    key={cat.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                    {selectedCategory === cat.id && (
                      <X className="w-3 h-3" />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-3">Service Type</div>
              <div className="flex flex-wrap gap-2">
                {serviceTypes.map(service => (
                  <motion.button
                    key={service.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedService(service.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                      selectedService === service.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <span>{service.icon}</span>
                    <span>{service.label}</span>
                    {selectedService === service.id && (
                      <X className="w-3 h-3" />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Count */}
      <div className="bg-gray-50 py-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-between">
            <div className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredPortfolio.length}</span> of{' '}
              <span className="font-semibold text-gray-900">{mockData.portfolio.length}</span> projects
            </div>
            <div className="text-sm text-gray-500">
              Sorted by: <span className="font-semibold text-gray-700">Latest First</span>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Grid/List */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <AnimatePresence mode="wait">
            {filteredPortfolio.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-20"
              >
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                  No projects found
                </h3>
                <p className="text-gray-600 max-w-md mx-auto mb-8">
                  Try adjusting your filters or search terms to find what you're looking for.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedService('all');
                    setSearchQuery('');
                  }}
                >
                  Reset Filters
                </Button>
              </motion.div>
            ) : viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                layout
              >
                {filteredPortfolio.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    layoutId={`project-${project.id}`}
                  >
                    <Card className="bg-white border-none hover:shadow-2xl transition-all duration-300 overflow-hidden group h-full flex flex-col">
                      {/* Project Header */}
                      <div className="relative h-48 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-400/10" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-6xl font-serif font-bold text-black opacity-10">
                            {project.client.split(' ').map(word => word[0]).join('')}
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        {/* Quick Actions */}
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <Button
                            size="icon"
                            className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full"
                            onClick={() => setSelectedProject(project)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Category Badge */}
                        <Badge className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800">
                          {project.category}
                        </Badge>
                      </div>

                      {/* Card Content */}
                      <CardHeader className="flex-grow">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <Badge variant="outline" className="mb-2 bg-blue-50 text-blue-700 border-blue-200">
                              {project.service}
                            </Badge>
                            <CardTitle className="font-serif text-xl text-black line-clamp-1">
                              {project.title}
                            </CardTitle>
                            <p className="text-sm text-gray-600 mt-1">{project.client} • {project.year}</p>
                          </div>
                        </div>
                        <CardDescription className="text-gray-700 line-clamp-3">
                          {project.description}
                        </CardDescription>
                      </CardHeader>
                      
                      <CardContent>
                        {/* Technologies */}
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {project.technologies.slice(0, 3).map((tech, idx) => (
                              <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-700 text-xs">
                                {tech}
                              </Badge>
                            ))}
                            {project.technologies.length > 3 && (
                              <Badge variant="secondary" className="bg-gray-100 text-gray-700 text-xs">
                                +{project.technologies.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* View Button */}
                        <Button
                          variant="outline"
                          className="w-full group/view"
                          onClick={() => setSelectedProject(project)}
                        >
                          <span>View Case Study</span>
                          <ArrowRight className="w-4 h-4 ml-2 group-hover/view:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
                layout
              >
                {filteredPortfolio.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-white border-none hover:shadow-lg transition-all duration-300 overflow-hidden">
                      <div className="flex flex-col md:flex-row">
                        {/* Thumbnail */}
                        <div className="md:w-48 lg:w-64 flex-shrink-0">
                          <div className="h-full min-h-[200px] md:min-h-full bg-gradient-to-br from-gray-100 to-gray-200 relative">
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-6xl font-serif font-bold text-black opacity-10">
                                {project.client.split(' ').map(word => word[0]).join('')}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-grow p-6">
                          <div className="flex flex-col lg:flex-row lg:items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className="bg-blue-100 text-blue-700">
                                  {project.category}
                                </Badge>
                                <Badge variant="outline">
                                  {project.service}
                                </Badge>
                              </div>
                              <CardTitle className="font-serif text-2xl text-black mb-2">
                                {project.title}
                              </CardTitle>
                              <p className="text-gray-600">{project.client} • {project.year}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-4 lg:mt-0"
                              onClick={() => setSelectedProject(project)}
                            >
                              View Details
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                          
                          <CardDescription className="text-gray-700 mb-4">
                            {project.description}
                          </CardDescription>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-2">
                              {project.technologies.slice(0, 5).map((tech, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-700">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                            
                            <div className="hidden md:flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {project.timeline || '3 Months'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Projects Completed' },
              { value: '250+', label: 'Happy Clients' },
              { value: '15+', label: 'Countries Served' },
              { value: '99%', label: 'Satisfaction Rate' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-primary-lime to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-6 bg-black text-white hover:bg-gray-800">
              🚀 Let's Build Together
            </Badge>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-black mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto">
              Let's create something amazing together. Share your vision and we'll bring it to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
               <a href="/#contact">
  <Button className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg">
    Get in Touch
    <ArrowRight className="w-5 h-5 ml-2" />
  </Button>
</a>

              <Button variant="outline" className="px-8 py-6 text-lg border-2 border-black">
                View All Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Portfolio;